<template>
  <b-form @submit.prevent="onSubmit" enctype="multipart/form-data">
    <b-form-group
      id="input-group-title"
      label="Job Title"
      label-for="input-title"
    >
      <b-form-input
        id="input-title"
        v-model="form.title"
        type="text"
        required
        placeholder="Enter Job Title"
        :state="state.title"
        aria-describedby="input-title-feedback"
      ></b-form-input>

      <b-form-invalid-feedback id="input-title-feedback">
        <!-- eslint-disable-next-line -->
        {{ errors.title }}
      </b-form-invalid-feedback>
    </b-form-group>

    <b-row>
      <b-col>
        <b-form-group
          id="input-group-category"
          label="Category"
          label-for="input-category"
        >
          <b-img
            class="append-icon"
            :src="require('@/assets/icons/chevron-secondary.svg')"
          ></b-img>
          <b-form-select
            id="input-category"
            ref="categorySelection"
            v-model="form.category_id"
            :options="categories"
            value-field="id"
            text-field="label"
            required
            @input="loadSubcategories"
            :style="!form.category_id ? 'color: #b2b2b2' : ''"
            :state="state.category_id"
            aria-invalid="input-category-feedback"
          >
            <template v-slot:first>
              <b-form-select-option :value="null"
                >Select Category</b-form-select-option
              >
            </template>
          </b-form-select>

          <b-form-invalid-feedback id="input-category-feedback">
            <!-- eslint-disable-next-line -->
            {{ errors.category_id }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group
          id="input-group-subcategory"
          label="Subcategory"
          label-for="input-subcategory"
        >
          <b-img
            class="append-icon"
            :src="require('@/assets/icons/chevron-secondary.svg')"
          ></b-img>
          <b-form-select
            id="input-subcategory"
            v-model="form.subcategory_id"
            :options="subcategories"
            value-field="id"
            text-field="label"
            required
            :style="!form.subcategory_id ? 'color: #b2b2b2' : ''"
            :state="state.subcategory_id"
            aria-invalid="input-subcategory-feedback"
          >
            <template v-slot:first>
              <b-form-select-option :value="null"
                >Select Subcategory</b-form-select-option
              >
            </template>
          </b-form-select>

          <b-form-invalid-feedback id="input-subcategory-feedback">
            <!-- eslint-disable-next-line -->
            {{ errors.subcategory_id }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group
          id="input-group-suburb"
          label="Suburb"
          label-for="input-suburb"
        >
          <place-autocomplete
            v-model="form.suburb"
            placeholder="Enter Suburb"
            :api-key="$env.placesApiKey"
            :input-state="state.suburb"
            :input-error="errors.suburb"
            input-class="w-100"
            :prepend-icon="false"
            @select="selectLocation"
          ></place-autocomplete>
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group
          id="input-group-price"
          label="Your Offer"
          label-for="input-price"
        >
          <div class="prepend-icon font-weight-bold">$</div>
          <b-form-input
            id="input-price"
            v-model="form.price_offer"
            type="text"
            required
            pattern="^\d{1,3}(,\d{3})*(\.\d+)?$"
            data-type="currency"
            placeholder="0.00"
            @keyup="formatCurrency($event)"
            @blur="formatCurrency($event, 'blur')"
            class="text-right"
            :state="state.price_offer"
            aria-describedby="input-price-feedback"
          ></b-form-input>

          <b-form-invalid-feedback id="input-price-feedback">
            <!-- eslint-disable-next-line -->
            {{ errors.price_offer }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <label for="textarea">Description</label>
    <b-form-textarea
      id="input-description"
      v-model="form.description"
      placeholder="Description"
      rows="3"
      :state="state.description"
      aria-describedby="input-description-feedback"
    ></b-form-textarea>

    <b-form-invalid-feedback id="input-description-feedback">
      <!-- eslint-disable-next-line -->
      {{ errors.description }}
    </b-form-invalid-feedback>

    <div class="page-subtitle mt-5 mb-4">Photos</div>
    <div
      class="upload-container mb-4"
      v-cloak
      @drop.prevent="job ? uploadPhotos($event) : loadPhotos($event)"
      @dragover.prevent
    >
      <b-img
        :src="require('@/assets/icons/image.png')"
        :srcset="`${require('@/assets/icons/image@2x.png')} 2x`"
        alt="Photos"
        v-bind="mainProps"
      ></b-img>

      <div class="upload-label mt-3">
        <b-button variant="primary" @click="launchFilePicker"
          >Upload photos</b-button
        >
      </div>

      <b-form-file
        ref="filePicker"
        multiple
        accept="image/*"
        style="display:none"
        @input="job ? uploadPhotos($event) : loadPhotos($event)"
        aria-invalid="input-photos-feedback"
      ></b-form-file>

      <label class="mt-3" variant="secondary">Or drag them in</label>
    </div>

    <b-form-invalid-feedback id="input-photos-feedback">
      <!-- eslint-disable-next-line -->
      {{ errors.photos }}
    </b-form-invalid-feedback>

    <div class="preview-container" v-if="previews.length !== 0">
      <div
        class="preview-item"
        :class="pIndex !== previews.length - 1 ? 'mr-3' : ''"
        v-for="(preview, pIndex) in previews"
        :key="pIndex"
      >
        <b-icon-x-circle-fill
          variant="dark"
          class="remove-icon"
          @click="onRemovePreview(pIndex)"
        ></b-icon-x-circle-fill>

        <b-img-lazy
          :src="preview"
          alt="Photos"
          v-bind="previewProps"
          class="h-100"
        ></b-img-lazy>
      </div>
    </div>

    <b-row class="mt-5">
      <b-col>
        <b-button
          block
          class="btn-outlined bg-white text-dark"
          variant="outline-secondary"
          @click="resetForm"
          >{{ job ? 'Cancel' : 'Discard' }}</b-button
        >
      </b-col>
      <b-col>
        <b-button block type="submit" variant="primary" :disabled="loading">
          <b-spinner small v-if="loading"></b-spinner>
          <div v-else>{{ job ? 'Update' : 'Post' }}</div>
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { findIndex } from 'lodash'
import PlaceAutocomplete from '@/components/autocomplete/PlaceAutocomplete'
import Compressor from 'compressorjs'

export default {
  components: {
    PlaceAutocomplete
  },

  computed: {
    ...mapState({
      job: state => state.job.jobDetails,
      categories: state => state.category.list
    })
  },

  data() {
    return {
      loading: false,
      mainProps: {
        center: true,
        width: 80
      },
      previewProps: {
        center: true,
        blank: true,
        blankColor: '#bbb'
      },
      subcategories: [],
      previews: [],
      form: {
        id: null,
        title: '',
        category_id: null,
        subcategory_id: null,
        photos: [],
        suburb: '',
        latitude: '',
        longitude: '',
        price_offer: '',
        description: ''
      },
      state: {
        title: null,
        category_id: null,
        subcategory_id: null,
        photos: null,
        suburb: null,
        price_offer: null,
        description: null
      },
      errors: {
        title: null,
        category_id: null,
        subcategory_id: null,
        photos: null,
        suburb: null,
        price_offer: null,
        description: null
      }
    }
  },

  methods: {
    ...mapActions({
      getCategories: 'category/getCategories',
      createJob: 'job/createJob',
      updateJob: 'job/updateJob'
    }),

    resetForm() {
      if (this.job) {
        // update form
        for (let key in this.form) {
          this.form[key] = this.job[key]
          this.previews = this.job.photos.map(p => p.url)
          this.form.photos = this.job.photos.map(p => {
            return {
              id: p.id,
              delete: false
            }
          })
        }
      } else {
        // create form
        this.form.id = null
        this.form.category_id = null
        this.form.subcategory_id = null
        this.form.title = ''
        this.form.suburb = ''
        this.form.latitude = ''
        this.form.longitude = ''
        this.form.description = ''
        this.form.price_offer = ''
        this.form.photos = []
      }

      for (let key in this.state) {
        this.state[key] = null
      }
      for (let key in this.errors) {
        this.errors[key] = null
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    },

    getFormData() {
      let formData = this.form // update form
      if (!this.job) {
        // create form
        formData = new FormData()
        formData.append('id', this.form.id)
        formData.append('title', this.form.title)
        formData.append('description', this.form.description)
        formData.append('category_id', this.form.category_id)
        formData.append('subcategory_id', this.form.subcategory_id)
        formData.append('price_offer', this.form.price_offer)
        formData.append('suburb', this.form.suburb)
        formData.append('latitude', this.form.latitude)
        formData.append('longitude', this.form.longitude)
        for (let i = 0; i < this.form.photos.length; i++) {
          formData.append('photos[]', this.form.photos[i])
        }
      }
      return formData
    },

    formatCurrency(e, blur) {
      let value = e.target.value
      if (value === '') return

      let caretPos = e.target.selectionStart
      const originalLen = value.length
      const decimalPos = value.indexOf('.')
      value = value.replace(/^\$0+/, '')
      if (decimalPos >= 0) {
        value =
          `${this.formatNumber(value.substring(0, decimalPos))}.` +
          (
            this.formatNumber(value.substring(decimalPos)) +
            (blur === 'blur' ? '00' : '')
          ).substring(0, 2)
      } else {
        value = `${this.formatNumber(value)}`
        if (blur === 'blur') {
          value += '.00'
        }
      }
      e.target.value = value === '.00' ? '' : value
      caretPos = value.length - originalLen + caretPos
      e.target.setSelectionRange(caretPos, caretPos)

      this.form.price_offer = e.target.value
    },

    formatNumber(n) {
      return n.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    loadSubcategories(v) {
      const index = findIndex(this.categories, { id: v })
      this.subcategories =
        index !== -1 ? this.categories[index].subcategories : []

      if (!this.job || this.job.category_id !== v) {
        this.form.subcategory_id = null // reset subcategory selection
      }
    },

    selectLocation(coordinates) {
      this.form.latitude = coordinates.lat
      this.form.longitude = coordinates.lng
    },

    launchFilePicker() {
      this.$refs.filePicker.$el.childNodes[0].click()
    },

    loadPhotos(e) {
      // create form
      let _this = this
      const photos = e.dataTransfer ? e.dataTransfer.files : e
      for (let i = 0; i < photos.length; i++) {
        const file = photos[i]
        const reader = new FileReader()
        reader.readAsDataURL(photos[i])
        reader.onload = e => {
          const base64Img = e.target.result
          this.previews.push(base64Img)

          if (file.size > 2000000) {
            const binaryImg = this.convertDataURIToBinary(base64Img)
            const blob = new Blob([binaryImg], { type: file.type })

            // eslint-disable-next-line
            new Compressor(blob, {
              quality: 0.2,
              success(result) {
                _this.addCompressedImage(result)
              }
            })
          } else {
            this.addCompressedImage(file)
          }
        }
      }
    },

    addCompressedImage(result) {
      this.form.photos.push(result)
    },

    convertDataURIToBinary(dataURI) {
      const BASE64_MARKER = ';base64,'
      const base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
      const base64 = dataURI.substring(base64Index)
      const raw = window.atob(base64)
      const rawLength = raw.length
      var array = new Uint8Array(new ArrayBuffer(rawLength))

      for (let i = 0; i < rawLength; i++) {
        array[i] = raw.charCodeAt(i)
      }
      return array
    },

    uploadPhotos(e) {
      // update form
      const photos = e.dataTransfer ? e.dataTransfer.files : e
      this.loading = true // disable submit if still uploading
      for (let i = 0; i < photos.length; i++) {
        this.uploadFile(photos[i], i === photos.length - 1)
      }
    },

    async uploadFile(photo, isLast) {
      // update form
      let data = new FormData()
      data.append('file', photo)
      await this.$api.post('media', data).then(({ data }) => {
        this.form.photos.push({ id: data.data.id })
        this.previews.push(data.data.url)
      })

      if (isLast) {
        // enable submit if done uploading
        this.loading = false
      }
    },

    onRemovePreview(index) {
      if (!this.job) {
        // create form
        this.form.photos.splice(index, 1)
      } else {
        // update form
        const isDelete = this.form.photos[index].delete
        if (isDelete === undefined) {
          // remove newly uploaded photo
          this.form.photos.splice(index, 1)
        } else if (isDelete === false) {
          // set existing photo's delete field to true
          this.form.photos[index].delete = true
        } else if (isDelete === true) {
          // find the new index of photo to be deleted and set it to true
          const falseIndex = findIndex(this.form.photos, { delete: false })
          this.form.photos[falseIndex + index].delete = true
        }
      }
      this.previews.splice(index, 1)
    },

    async onSubmit() {
      this.loading = true
      // remove price offer's comma
      this.form.price_offer = this.form.price_offer.replace(/,/g, '')
      if (this.job) {
        // update form
        await this.updateJob(this.getFormData())
        this.$router.replace({
          name: 'job.view-job-page',
          params: {
            id: this.job.id,
            title: this.form.title.replace(/ +/g, '-').toLowerCase()
          }
        })
      } else {
        // create form
        await this.createJob(this.getFormData())
        this.$router.replace({
          name: 'job.my-jobs-page'
        })
      }
      this.loading = false
    }
  },

  async created() {
    await this.getCategories()
    this.resetForm()
  },

  watch: {
    'form.title'() {
      this.state.title = this.form.title.length !== 0
      this.errors.title = 'This field is required'
    },
    'form.category_id'() {
      this.state.category_id = this.form.category_id !== null
      this.errors.category_id = 'This field is required'
    },
    'form.subcategory_id'() {
      this.state.subcategory_id = this.form.subcategory_id !== null
      this.errors.subcategory_id = 'This field is required'
    },
    'form.suburb'() {
      this.state.suburb = this.form.suburb.length !== 0
      this.errors.suburb = 'This field is required'
    },
    'form.price_offer'() {
      this.state.price_offer = this.form.price_offer.length !== 0
      this.errors.price_offer = 'This field is required'
    },
    'form.description'() {
      this.state.description = this.form.description.length !== 0
      this.errors.description = 'This field is required'
    },
    'form.photos'() {
      this.state.photos = this.form.photos.length !== 0
      this.errors.photos = 'This field is required'
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-select {
  background: none;
}
.btn {
  height: 56px;
}
.remove-icon {
  position: absolute;
  right: 6px;
  top: 6px;
  cursor: pointer;
}
.preview-item {
  width: 180px;
  height: 180px;
}
</style>
